import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Textarea } from '@/components/atoms/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/molecules/form-field';
import type { CreateShopFormData, UpdateShopFormData } from '../types/shop-form.types';

interface ShopBasicInfoProps<T extends CreateShopFormData | UpdateShopFormData> {
  form: UseFormReturn<T>;
}

export function ShopBasicInfo<T extends CreateShopFormData | UpdateShopFormData>({
  form,
}: ShopBasicInfoProps<T>) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shops.basicInfo')}</CardTitle>
        <CardDescription>{t('shops.basicInfoDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name={'name' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.shopName')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('shops.shopNamePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'manager' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.manager')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('shops.managerPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'description' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.description')} *</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('shops.descriptionPlaceholder')}
                  rows={4}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'phoneNumber' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.phone')} *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+33 6 12 34 56 78" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'delivery' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.deliveryType')} *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('shops.selectDeliveryType')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="store">{t('shops.deliveryTypes.store')}</SelectItem>
                  <SelectItem value="home">{t('shops.deliveryTypes.home')}</SelectItem>
                  <SelectItem value="both">{t('shops.deliveryTypes.both')}</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>{t('shops.deliveryTypeDescription')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'tags' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.tags')}</FormLabel>
              <FormControl>
                <Input placeholder={t('shops.tagsPlaceholder')} {...field} />
              </FormControl>
              <FormDescription>{t('shops.tagsHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
